/**
 * FLAGS for the sync-to-REST migration (plan `docs/plans/06-migracion-rest.md`).
 *
 * Each migration stage carries a flag that gates the **client's read path**
 * and acts as an immediate rollback lever while the equivalent PowerSync
 * stream stays alive. A flag does NOT trim a stream (explicit decision in
 * the plan, §7): the trim travels in a later commit and its rollback is
 * `git revert` + `rollout restart`.
 *
 * IMPORTANT — what these flags do NOT gate: access control. Redacting the
 * email by capability (`org_rbac::can_view_member_emails`) is
 * UNCONDITIONAL on the server. Hanging it off a flag would have turned the
 * rollback switch into a switch to reopen the leak, which is exactly the
 * defect the stage closes. That's why these features are NOT `gateable`:
 * they have —and must have— no `feature_gate` in any write-path.
 */

import type { FeatureManifest } from "./country/packs";

/**
 * E2 · member identities via REST instead of via the `organizations` stream.
 *
 * The defect it closes (D3) wasn't the transport but the filter: the stream
 * handed out every staff member's and every classmate's email to any
 * member. With the flag on, the directory's and the member card's identity
 * is served via `GET /api/org/listMembers`, which does gate the email by
 * capability; with it off, the client goes back to reading the already-
 * replicated identity (the rollback lever, which dies with the stream trim in E3).
 *
 * **E3 reuses it** instead of minting a new flag: the four screens still
 * left with an identity join (cohort roster, groups, attendance report and
 * alerts) and the directory with the record switch the SAME decision —"does
 * third-party identity come from the server or the local mirror?"— over the
 * same `"user"` rows. Two switches for one decision only serve to leave half
 * the migration switched on.
 */
const REST_IDENTITIES: FeatureManifest = {
  key: "academy.data.rest_identities",
  name: "Identidades de miembros por API",
  description:
    "Sirve el nombre, la imagen y —solo a quien puede gestionar miembros o leer datos de sede— el correo de los miembros de la academia por API en vez de replicarlos a cada dispositivo. Un alumno deja de recibir los correos de sus compañeros y del personal.",
  scope: "org",
  status: "beta",
  since: "2026-07",
  docsSlug: "administrador",
  // No hard edges: member identity doesn't depend on any other feature, and
  // no other feature should be held hostage to this rollback lever.
  requires: [],
  implications: {
    navHidden: [],
    flowsBlocked: [
      "Hidratar nombre/correo del directorio de gente desde la API (se vuelve al dato replicado)",
      "Hidratar la identidad de la ficha de miembro desde la API",
      "Hidratar la identidad del roster, el informe de asistencia y las alertas de una cohorte desde la API",
      "Servir el directorio con expediente desde la API (se vuelve a los perfiles replicados)",
    ],
    dataPolicy: "preserved",
  },
};

/** Manifests of the data-access migration (source `data_access`). */
export function dataAccessManifests(): FeatureManifest[] {
  return [REST_IDENTITIES];
}

/** Key of the E2 flag (greppable from the client). */
export const FLAG_REST_IDENTITIES = REST_IDENTITIES.key;
